import type { ApolloQueryResult, ApolloError } from 'apollo-client';
import type {
	SprintState,
	SprintField,
} from '@atlassian/jira-business-common/src/common/types/sprint.tsx';
import type { COMMENT_SUMMARY_TYPE } from '@atlassian/jira-business-constants';
import type { TransformedDevSummary } from '@atlassian/jira-business-fields/src/common/field-types/dev-summary/types.tsx';
import type {
	TransformedPriorityField,
	TransformedPriority,
} from '@atlassian/jira-business-fields/src/common/field-types/priority/types.tsx';
import type { SUPPORTED_FIELD_TYPES } from '@atlassian/jira-business-fields/src/common/supported-field-types/index.tsx';
import type { ListIssueFieldTransformed as ListIssueFieldTransformedNew } from '@atlassian/jira-business-fields/src/common/types.tsx';
import type { JWMGetItemsCacheVariables } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/types.tsx';
import type {
	DUE_DATE_TYPE,
	SELECT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	TIME_ESTIMATE_TYPE,
	NUMBER_CF_TYPE,
	MULTI_USER_CF_TYPE,
	COMPONENTS_TYPE,
	STORY_POINTS_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	PEOPLE_CF_TYPE,
	SUBTASKS_TYPE,
	CHILDREN_ISSUES,
	PARENT_TYPE,
	LABELS_TYPE,
	PRIORITY_TYPE,
	USER_CF_TYPE,
	LABELS_CF_TYPE,
	STATUS_TYPE,
	URL_CF_TYPE,
	TEXT_AREA_CF_TYPE,
	ISSUE_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	SPRINT_TYPE,
} from '@atlassian/jira-platform-field-config';
import type { DateOnly } from '@atlassian/jira-platform-utils-date-only';
import type {
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_legacyConfluenceLinks as ConfluenceLinks,
	JWMListViewItems,
} from './__generated_apollo__/JWMListViewItems';
import type { ListViewIssueFieldsFragment_fields_JiraSingleSelectField_fieldOption_color as ListViewFieldOptionColor } from './__generated_apollo__/ListViewIssueFieldsFragment';

export type ListViewIssue = {
	id: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	issueId: any;
	fields?: ListViewIssueFieldsTransformed;
	typeId?: string;
	issuetype: {
		id?: string;
		iconUrl?: string;
		name?: string;
		level?: number;
		issueTypeId?: string;
	};
	key: string;
	rank: string;
	summary?: string;
	timeoriginalestimate?: TimeOriginalEstimate;
	status?: {
		id?: string;
		name: string;
		categoryId: string;
	};
	priority?: {
		id: string | null;
		name: string | null;
		iconUrl: string | null;
	};
	labels?: string[];
	created?: DateOnly;
	duedate?: DateOnly;
	updated?: DateOnly;
	assignee?: {
		accountId: string;
		displayName: string;
		avatarUrl: string;
	};
	category?: {
		fieldKey: string;
		name: string;
		optionId: string;
		color: string | null;
	};
	totalSubtasks?: number;
	totalGroupItems?: number;
	parentIssueId?: string;
	confluenceLinks?: ConfluenceLinks;
	cacheVariables: JWMGetItemsCacheVariables;
	childSummary?: {
		hasChildren?: boolean;
		totalCount?: number;
	};
	commentSummary?: {
		totalCount?: number;
	};
	devSummary?: TransformedDevSummary | null;
	// storypoints, storypointestimate and sprint are used in grouping
	storypoints?: number;
	storypointestimate?: number;
	sprint?: SprintField | null;
};

export type ListViewIssuesReturn = {
	loading: ApolloQueryResult<JWMListViewItems>['loading'];
	error?: ApolloError;
	data: ListViewIssue[];
	loadNextPage: () => void;
	isLoadingNextPage: boolean;
	hasNextPage?: boolean;
};

export type DateTimeFields = ListIssueDateTimeField | ListIssueDateTimeCustomField;

export type ListViewIssueFieldsTransformed = ListIssueFieldTransformed[];

type ISSUE_FIELD_TYPE_ISSUE_TYPE = typeof ISSUE_TYPE;
type ISSUE_FIELD_TYPE_ISSUE_KEY = string;
type ISSUE_FIELD_TYPE_TEXT_FIELD = string;
type ISSUE_FIELD_TYPE_URL_CF = typeof URL_CF_TYPE;
type ISSUE_FIELD_TYPE_TEXT_FIELD_CF = string;
type ISSUE_FIELD_TYPE_TEXT_AREA_CF = typeof TEXT_AREA_CF_TYPE;
type ISSUE_FIELD_TYPE_STATUS = typeof STATUS_TYPE;
type ISSUE_FIELD_TYPE_USER = string;
type ISSUE_FIELD_TYPE_COMMENT_SUMMARY = typeof COMMENT_SUMMARY_TYPE;
type ISSUE_FIELD_TYPE_USER_PICKER_CF = typeof USER_CF_TYPE;
type ISSUE_FIELD_TYPE_LABELS_CF = typeof LABELS_CF_TYPE;
type ISSUE_FIELD_TYPE_LABELS = typeof LABELS_TYPE;
type ISSUE_FIELD_TYPE_PRIORITY = typeof PRIORITY_TYPE;
type ISSUE_FIELD_TYPE_DATE = typeof DUE_DATE_TYPE;
type ISSUE_FIELD_TYPE_DATE_TIME = string;
type ISSUE_FIELD_TYPE_SELECT_CF = typeof SELECT_CF_TYPE;
type ISSUE_FIELD_TYPE_MULTI_SELECT_CF = typeof MULTI_SELECT_CF_TYPE;
type ISSUE_FIELD_TYPE_JWM_CATEGORY_CF = string;
type ISSUE_FIELD_TYPE_TIME_ORIGINAL_ESTIMATE = typeof TIME_ESTIMATE_TYPE;
type ISSUE_FIELD_TYPE_DATE_PICKER_CF = string;
type ISSUE_FIELD_TYPE_FLOAT_CF = typeof NUMBER_CF_TYPE;
type ISSUE_FIELD_TYPE_STORY_POINTS = typeof STORY_POINTS_TYPE;
type ISSUE_FIELD_TYPE_STORY_POINT_ESTIMATE_CF_TYPE = typeof STORY_POINT_ESTIMATE_CF_TYPE;
type ISSUE_FIELD_TYPE_DATE_TIME_CF = string;
type ISSUE_FIELD_TYPE_MULTI_USER_PICKER_CF = typeof MULTI_USER_CF_TYPE;
type ISSUE_FIELD_TYPE_PEOPLE_CF = typeof PEOPLE_CF_TYPE;
type ISSUE_FIELD_TYPE_PROJECT_COMPONENTS = typeof COMPONENTS_TYPE;
type ISSUE_FIELD_TYPE_SUBTASKS = typeof SUBTASKS_TYPE;
type ISSUE_FIELD_TYPE_CHILD_ISSUES = typeof CHILDREN_ISSUES;
type ISSUE_FIELD_TYPE_PARENT = typeof PARENT_TYPE;
type ISSUE_FIELD_TYPE_MULTI_CHECKBOXES_CF = typeof MULTI_CHECKBOXES_CF_TYPE;
type ISSUE_FIELD_TYPE_SPRINT = typeof SPRINT_TYPE;

export type ListIssueFieldType =
	| ISSUE_FIELD_TYPE_ISSUE_TYPE
	| ISSUE_FIELD_TYPE_ISSUE_KEY
	| ISSUE_FIELD_TYPE_TEXT_FIELD
	| ISSUE_FIELD_TYPE_URL_CF
	| ISSUE_FIELD_TYPE_TEXT_FIELD_CF
	| ISSUE_FIELD_TYPE_TEXT_AREA_CF
	| ISSUE_FIELD_TYPE_STATUS
	| ISSUE_FIELD_TYPE_USER
	| ISSUE_FIELD_TYPE_COMMENT_SUMMARY
	| ISSUE_FIELD_TYPE_USER_PICKER_CF
	| ISSUE_FIELD_TYPE_LABELS_CF
	| ISSUE_FIELD_TYPE_LABELS
	| ISSUE_FIELD_TYPE_PRIORITY
	| ISSUE_FIELD_TYPE_DATE
	| ISSUE_FIELD_TYPE_DATE_TIME
	| ISSUE_FIELD_TYPE_SELECT_CF
	| ISSUE_FIELD_TYPE_MULTI_SELECT_CF
	| ISSUE_FIELD_TYPE_JWM_CATEGORY_CF
	| ISSUE_FIELD_TYPE_TIME_ORIGINAL_ESTIMATE
	| ISSUE_FIELD_TYPE_DATE_PICKER_CF
	| ISSUE_FIELD_TYPE_FLOAT_CF
	| ISSUE_FIELD_TYPE_STORY_POINTS
	| ISSUE_FIELD_TYPE_STORY_POINT_ESTIMATE_CF_TYPE
	| ISSUE_FIELD_TYPE_DATE_TIME_CF
	| ISSUE_FIELD_TYPE_MULTI_USER_PICKER_CF
	| ISSUE_FIELD_TYPE_PEOPLE_CF
	| ISSUE_FIELD_TYPE_PROJECT_COMPONENTS
	| ISSUE_FIELD_TYPE_SUBTASKS
	| ISSUE_FIELD_TYPE_CHILD_ISSUES
	| ISSUE_FIELD_TYPE_PARENT
	| ISSUE_FIELD_TYPE_MULTI_CHECKBOXES_CF
	| SUPPORTED_FIELD_TYPES;

// TODO: go/restrict-enums
// eslint-disable-next-line no-restricted-syntax
export enum AccountStatus {
	active = 'active',
	closed = 'closed',
	inactive = 'inactive',
}

export type UserNode = {
	readonly __typename: 'AtlassianAccountUser';
	readonly name: string | null;
	readonly picture: string | null;
	readonly accountId: string | null;
	readonly accountStatus: AccountStatus;
} | null;

export type User = {
	node: UserNode | null;
} | null;

export type TransformedUser = {
	__typename?: string;
	accountId: string;
	name: string;
	accountStatus?: string;
	picture: string;
} | null;

export type TimeOriginalEstimate = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeInSeconds: any | null;
};

export type TransformedTimeOriginalEstimate = {
	timeAsString?: string;
	timeInSeconds?: number;
};

export type SelectFieldSelectedOption = {
	node: SelectFieldSelectedOptionNode;
};

export type FieldOptionColor = ListViewFieldOptionColor | null;

export type SelectFieldSelectedOptionNode = {
	__typename: 'JiraOption';
	optionId: string;
	value: string | null;
	color?: FieldOptionColor | null;
} | null;

export type TransformedSelectCustomFieldSelectedValue = {
	optionId: string;
	value: string | null;
	color?: string | null;
} | null;

type ProjectComponentValue = {
	node: {
		name: string | null;
		componentId: string;
	} | null;
};

export type ProjectComponentValues = ReadonlyArray<ProjectComponentValue | null> | null;

export type TransformedProjectComponentValues = Array<{
	name: string | null;
	componentId: string;
} | null>;

export type ParentIssue = {
	issueId: string;
	key: string;
};

export type TransformedParentIssue = {
	issueId: string | null;
	issueKey: string | null;
};

export type Labels = ReadonlyArray<{
	node: {
		__typename: 'JiraLabel';
		name: string | null;
	} | null;
} | null> | null;

export type Sprints = ReadonlyArray<{
	node: {
		__typename: 'JiraSprint';
		name: string | null;
		sprintId: string;
		state: SprintState | null;
	} | null;
} | null> | null;

export type TransformedLabels = ReadonlyArray<string | null>;

export type TransformedSprint = Array<{
	name: string | null;
	sprintId: string;
	state: SprintState | null;
	__typename: 'JiraSprint';
} | null>;

type ValueType =
	| string
	| number
	| string[]
	| ReadonlyArray<string>
	| TransformedUser
	| TransformedUser[]
	| TransformedSelectCustomFieldSelectedValue
	| TransformedSelectCustomFieldSelectedValue[]
	| TransformedTimeOriginalEstimate
	| TransformedProjectComponentValues
	| TransformedLabels
	| TransformedParentIssue
	| TransformedSprint
	| TransformedPriority;

type BaseListIssueField<T extends ListIssueFieldType, VT extends ValueType> = {
	fieldId: string;
	type: T;
	value: VT | null;
};

export type ListIssueIssueKeyField = BaseListIssueField<ISSUE_FIELD_TYPE_TEXT_FIELD, string>;
export type ListIssueIssueTypeField = BaseListIssueField<ISSUE_FIELD_TYPE_ISSUE_TYPE, string> & {
	level?: number | null;
	issueTypeId?: string | null;
};
export type ListIssueTextField = BaseListIssueField<ISSUE_FIELD_TYPE_TEXT_FIELD, string>;
export type ListIssueUrlCustomField = BaseListIssueField<ISSUE_FIELD_TYPE_URL_CF, string>;
export type ListIssueTextFieldCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_TEXT_FIELD_CF,
	string
>;
export type ListIssueTextAreaCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_TEXT_AREA_CF,
	string
>;
export type ListIssueStatusField = BaseListIssueField<ISSUE_FIELD_TYPE_STATUS, number> & {
	name: string | null;
};
export type ListIssueUserField = BaseListIssueField<ISSUE_FIELD_TYPE_USER, TransformedUser>;
export type ListIssueCommentSummaryField = BaseListIssueField<
	ISSUE_FIELD_TYPE_COMMENT_SUMMARY,
	number
>;
export type ListIssueUserPickerCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_USER_PICKER_CF,
	TransformedUser
>;
export type ListIssueLabelsCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_LABELS_CF,
	TransformedLabels
>;

export type ListIssueLabelsField = BaseListIssueField<ISSUE_FIELD_TYPE_LABELS, TransformedLabels>;
export type ListIssuePriorityFieldOld = BaseListIssueField<ISSUE_FIELD_TYPE_PRIORITY, string> & {
	priorityId: string | null;
};
export type ListIssuePriorityField = TransformedPriorityField;

export type ListIssueDateField = BaseListIssueField<ISSUE_FIELD_TYPE_DATE, string>;
export type ListIssueDateTimeField = BaseListIssueField<ISSUE_FIELD_TYPE_DATE_TIME, string>;
export type ListIssueSelectCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_SELECT_CF,
	TransformedSelectCustomFieldSelectedValue
>;
export type ListIssueMultiSelectCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_MULTI_SELECT_CF,
	TransformedSelectCustomFieldSelectedValue[]
>;
export type ListIssueJWMCategoryCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_JWM_CATEGORY_CF,
	TransformedSelectCustomFieldSelectedValue
>;
export type ListIssueTimeOriginalEstimateField = BaseListIssueField<
	ISSUE_FIELD_TYPE_TIME_ORIGINAL_ESTIMATE,
	TransformedTimeOriginalEstimate
>;
export type ListIssueDatePickerCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_DATE_PICKER_CF,
	string
>;
export type ListIssueDateTimeCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_DATE_TIME_CF,
	string
>;
export type ListIssueMultiUserPickerCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_MULTI_USER_PICKER_CF,
	TransformedUser[]
>;
export type ListIssuePeopleCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_PEOPLE_CF,
	TransformedUser[]
>;
export type ListIssueProjectComponentsField = BaseListIssueField<
	ISSUE_FIELD_TYPE_PROJECT_COMPONENTS,
	TransformedProjectComponentValues
>;

export type ListIssueParentField = BaseListIssueField<
	ISSUE_FIELD_TYPE_PARENT,
	TransformedParentIssue
>;
export type ListIssueEpicLinkField = {
	fieldId: 'epiclink';
	type?: null; // We need to enforce the type to at least exists in the type
	epic: {
		id: string;
	};
};
export type ListIssueMultiCheckboxesCustomField = BaseListIssueField<
	ISSUE_FIELD_TYPE_MULTI_CHECKBOXES_CF,
	TransformedSelectCustomFieldSelectedValue[]
>;

export type ListIssueSprintField = BaseListIssueField<ISSUE_FIELD_TYPE_SPRINT, TransformedSprint>;

export type ListIssueFieldTransformed =
	| ListIssueIssueTypeField
	| ListIssueIssueKeyField
	| ListIssueTextField
	| ListIssueUrlCustomField
	| ListIssueTextFieldCustomField
	| ListIssueTextAreaCustomField
	| ListIssueStatusField
	| ListIssueUserField
	| ListIssueCommentSummaryField
	| ListIssueUserPickerCustomField
	| ListIssueLabelsCustomField
	| ListIssueLabelsField
	| ListIssuePriorityFieldOld
	| ListIssuePriorityField
	| ListIssueDateField
	| ListIssueDateTimeField
	| ListIssueSelectCustomField
	| ListIssueMultiSelectCustomField
	| ListIssueJWMCategoryCustomField
	| ListIssueTimeOriginalEstimateField
	| ListIssueDatePickerCustomField
	| ListIssueDateTimeCustomField
	| ListIssueMultiUserPickerCustomField
	| ListIssuePeopleCustomField
	| ListIssueProjectComponentsField
	| ListIssueParentField
	| ListIssueEpicLinkField
	| ListIssueMultiCheckboxesCustomField
	| ListIssueSprintField
	| ListIssueFieldTransformedNew;
